import React from 'react';
import Page from './Page';
import Header from './Header';
import EditableField from './EditableField';
import { InformeData } from '../types';

interface InformeSeguimientoViewProps {
  data: InformeData;
  onFieldChange: (key: string, value: string) => void;
  onPatientChange: (field: 'nombre' | 'fecha', value: string) => void;
}

const InformeSeguimientoView: React.FC<InformeSeguimientoViewProps> = ({ data, onFieldChange, onPatientChange }) => {
  const getFieldValue = (key: string) => data.campos[key] || '';

  return (
    <div className="doc">
      {/* ========= PÁGINA 1 - INTRODUCCIÓN RECORTADA ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              {/* Nombre del paciente */}
              <div className="patient-row">
                <EditableField
                  value={data.paciente.nombre}
                  onChange={(value) => onPatientChange('nombre', value)}
                  fieldKey="nombre_paciente"
                  placeholder="Nombre del paciente"
                  className="inline"
                />
              </div>
              
              {/* Intro */}
              <p><strong>CIP</strong> es una entidad especializada en ofrecer servicios integrales de cuidado de personas, destacándose por su enfoque personalizado.</p>
            </div>
          </div>
        </header>

        <main className="content">
          <br />
          
          <p>A través de su unidad <em>Cuidado Integral de Personas</em>, se centra en proporcionar <strong>calidad de vida</strong> tanto para personas que requieren cuidados especiales con enfermedades ya de base (cáncer, enf. inmunológicas, metabólicas) como para aquellos que buscan mejorar su estilo de vida y longevidad.</p>

          {/* Mensaje central en rojo */}
          <p style={{textAlign: 'center', fontWeight: 'bold', color: '#c00'}}>
            Lo que ofrecemos es un cambio de estilo de vida.
          </p>

          {/* Explicación estilo de vida */}
          <p>La medicina y el estilo de vida están profundamente interconectados, ya que las decisiones cotidianas que tomamos sobre nuestra salud y bienestar emocional pueden tener un impacto significativo en nuestra calidad de vida y en la prevención de enfermedades y longevidad.</p>

          <p>Un estilo de vida saludable implica tomar decisiones conscientes sobre <strong>alimentación</strong>, <strong>ejercicio</strong>, <strong>descanso</strong>, <strong>manejo del estrés</strong> y hábitos sociales "tribu".</p>

          {/* Epigenética */}
          <h3>¿Por qué es tan importante el estilo de vida? ¿Cómo nos influye?</h3>

          {/* Imagen epigenética a la derecha */}
          <figure className="figure-right avoid-break">
            <img src="/img/imagen_1.jpeg" alt="Epigenética: ADN y Ambiente" />
          </figure>

          <p>La respuesta es la <strong>epigenética</strong>, un campo fascinante de la biología que estudia los cambios en la expresión genética que no implican alteraciones en la secuencia del ADN, pero que pueden influir en cómo se activan o desactivan los genes.</p>

          <p>Estos cambios epigenéticos son clave para entender cómo factores ambientales —como la alimentación, el estrés, el ejercicio y otros aspectos del estilo de vida— pueden afectar la salud a lo largo de toda la vida y, en algunos casos, incluso ser transmitidos a las siguientes generaciones.</p>

          <p>En rasgos generales podemos hablar de un <strong>20% genética</strong> y un <strong>80% epigenética</strong>: la genética establece la predisposición y la epigenética determina el grado en que esa predisposición se manifiesta.</p>

          <p>Los factores ambientales, incluidos la dieta, la actividad física, la exposición a toxinas (como el humo del tabaco), el estrés y la falta de sueño, pueden modificar la forma en que se expresan nuestros genes, incluso si no alteran la secuencia genética subyacente. Esto significa que nuestro estilo de vida puede influir en nuestra biología a un nivel muy profundo.</p>
        </main>
      </Page>

      {/* ========= PÁGINA 2 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <p>
                Muchas formas de cáncer están relacionadas con cambios epigenéticos. Por ejemplo,
                ciertos genes supresores de tumores pueden ser "apagados" por modificaciones epigenéticas,
                mientras que otros genes que promueven el crecimiento tumoral pueden ser "activados".
              </p>
            </div>
          </div>
        </header>

        <main className="content">
          <div className="two-col">
            <div className="col">
              <p>
                Enfermedades como la depresión, la esquizofrenia y el trastorno bipolar se han
                relacionado con alteraciones epigenéticas, especialmente en cómo los genes responden a
                factores estresantes y a los cambios en el ambiente. Las modificaciones epigenéticas pueden influir en el metabolismo y en la inflamación,
                contribuyendo al desarrollo de condiciones como la diabetes tipo 2, la hipertensión y la
                aterosclerosis; así como no olvidar el sistema inmunológico, que responde a los propios
                tejidos del cuerpo, lo que puede contribuir al desarrollo de enfermedades autoinmunes.
              </p>

              {/* Imagen central */}
              <figure className="figure-wide avoid-break" style={{maxWidth: '160mm', margin: '6mm auto'}}>
                <img src="/img/imagen_2.jpeg" alt="Hábitos de vida y efectos epigenéticos" />
              </figure>

              <p style={{textAlign: 'center', fontWeight: '700', color: '#c00', margin: '6mm 0'}}>
                La epigenética subraya la increíble capacidad que tenemos para
                "modificar nuestra biología a través de nuestras elecciones diarias".
              </p>

              <p>
                Un enfoque multidisciplinar permite abordar enfermedades complejas que requieren la
                intervención de diferentes especialistas para obtener una solución completa, aunando la
                medicina convencional con estrategias complementarias.
              </p>

              <p>
                CIP está formado por un conjunto de especialistas sanitarios: médicos especialistas en
                oncología médica, medicina interna y medicina de familia, fisioterapeutas, psicología y
                terapia, preparadores físicos, PNI y podólogos.
              </p>

              <p>
                Buscamos tratar al paciente de manera integral, considerando no solo los síntomas físicos,
                sino también sus necesidades emocionales, sociales y psicológicas.
              </p>

              <p>
                Cuando un paciente ingiere varios medicamentos, nuestro personal médico analiza con un
                software especializado las posibles interacciones entre dichos medicamentos u otras
                sustancias de consumo habitual, como pueden ser infusiones, especias, alimentos, etc. El
                objetivo de este estudio es prevenir efectos secundarios indeseados y optimizar la función
                de los medicamentos.
              </p>
            </div>
          </div>
        </main>
      </Page>

      {/* ========= PÁGINA 3 ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <p style={{fontWeight: '700'}}>
                Tus hábitos de vida te han llevado a estar en la situación actual, por lo que un cambio de estos hábitos será la ruta a la mejoría.
              </p>
            </div>
          </div>
        </header>

        <main className="content">
          <br />

          <p>
            Este cambio de estilo de vida puede conllevar también un cambio de hábito alimenticio. Una dieta implica un día de inicio y un día de fin.
            ¿Quieres estar saludable durante unos meses, o prefieres estar saludable toda la vida?
          </p>

          {/* Cita/aviso en una caja suave */}
          <div style={{background: '#f7eaea', borderLeft: '3px solid #c00', padding: '4mm 5mm', margin: '4mm 0', textAlign: 'center', fontWeight: '700', color: '#333'}}>
            No podemos pretender que las cosas cambien si seguimos haciendo lo mismo.<br />
            <span style={{fontWeight: '400', fontStyle: 'italic'}}>(Albert Einstein, físico)</span>
          </div>

          <p>
            Por estos motivos CIP te recomendamos encarecidamente que tengas una alimentación extremadamente variada y no pases por alto nuestras recomendaciones.
          </p>

          <p>
            Es por eso que los alimentos catalogados como <strong>no recomendados</strong> son alimentos que deberías evitar no sólo mientras te encuentres en fase de recuperación, sino como medida preventiva. De la misma manera los alimentos que recomendamos consumir frecuentemente deberías de incorporarlos a tu alimentación incluso una vez superada la fase de recuperación, ya que igual de perjudicial es alimentarse con algo potencialmente tóxico o dañino, que dejar de recibir nutrientes o micronutrientes. Las dos situaciones generan enfermedad. La clave de la salud es, entre otros motivos, consumir cuanta más diversidad de alimentos saludables mejor.
          </p>

          <p style={{textTransform: 'uppercase', color: '#c00', fontWeight: '700', textDecoration: 'underline', marginTop: '6mm'}}>
            Estrategias de tratamientos basadas en evidencia científica:
          </p>

          <p>
            Las estrategias que te recomendamos para tu tratamiento te pueden parecer exageradas y que van en contra de nuestro estado de confort, pero su utilidad terapéutica está más que demostrada científicamente. Su enfoque no es restablecer un ritmo fisiológico, generar un cambio metabólico e inmunológico y estimular a un área del cerebro que se denomina hipotálamo, que se encarga del grado de mantener estables casi todos los parámetros importantes para mantener una salud óptima.
          </p>

          <p>
            Esto lo conseguimos con diferentes estrategias: alimentación, medicación, actividad física, hábitos de vida, ejercicios respiratorios, ejercicios de temperatura… Posiblemente te recomendemos cambios que te resultarán "raros" para todavía la mayoría de la sociedad de hoy en día, pero todos ellos estarán justificados científicamente. <strong>CONFÍA.</strong>
          </p>

          <p style={{textAlign: 'center', marginTop: '6mm'}}>
            Te la detallamos a continuación.
          </p>
        </main>
      </Page>

      {/* ========= PÁGINA 4 - RECOMENDACIONES A SEGUIR ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <h3 style={{color: '#c00', margin: '0 0 6mm 0'}}>RECOMENDACIONES A SEGUIR:</h3>
            </div>
          </div>
        </header>

        <main className="content">
          <br />

          <EditableField
            value={getFieldValue('recomendaciones_seguir')}
            onChange={(value) => onFieldChange('recomendaciones_seguir', value)}
            fieldKey="recomendaciones_seguir"
            multiline
            className="large-textarea"
            placeholder="Recomendaciones a seguir..."
          />
        </main>
      </Page>

      {/* ========= PÁGINA 5 - SUPLEMENTACIÓN ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" style={{marginTop: '-8mm'}} />
            <div className="header-title">
              <h3 style={{color: '#c00', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4mm'}}>
                Suplementación:
              </h3>
              <p>
                Los suplementos que te indicamos a continuación son productos avalados científicamente,
                no interaccionan con ningún tipo de fármaco que estés tomando. Igualmente no generan
                adicción, pretenden restablecer algunos nutrientes, ayudar a mitigar los efectos secundarios de
                los tratamientos farmacológicos sin restarle eficacia, aumentar la modulación de nuestro sistema inmune o simplemente intentar o ayudar a frenar directamente tu patología. Los puedes encontrar o encargar en cualquier parafarmacia y herbolario especializado.
              </p>
            </div>
          </div>
        </header>

        <main className="content">
          <br /><br /><br /><br />

          <EditableField
            value={getFieldValue('suplementacion')}
            onChange={(value) => onFieldChange('suplementacion', value)}
            fieldKey="suplementacion"
            multiline
            placeholder="Suplementación..."
            className="large-textarea"
          />
          <br />
        </main>
      </Page>

      {/* ========= PÁGINA 6 - MEDICACIÓN ========= */}
      <Page>
        <header className="header">
          <div className="header-wrap">
            <img className="header-logo" src="/img/logo.png" alt="CIP — Cuidado Integral de Personas" />
            <div className="header-title">
              <h3 style={{color: '#c00', margin: '0 0 6mm 0'}}>MEDICACIÓN:</h3>
            </div>
          </div>
        </header>

        <main className="content">
          <br />

          <EditableField
            value={getFieldValue('medicacion')}
            onChange={(value) => onFieldChange('medicacion', value)}
            fieldKey="medicacion"
            multiline
            className="large-textarea"
            placeholder="Medicación..."
          />

          {/* Aviso importante */}
          <div style={{
            margin: '4mm auto 0',
            padding: '2mm 4mm',
            width: '80%',
            border: '0.35mm solid #e0c5a5',
            background: '#fff6ee',
            borderRadius: '2mm',
            fontSize: '9pt',
            lineHeight: '1.2',
            color: '#c00',
            textAlign: 'center'
          }}>
            <div style={{fontWeight: 'bold', color: '#c00', marginBottom: '1mm', fontSize: '10pt'}}>
              IMPORTANTE
            </div>
            <div style={{fontSize: '9pt', lineHeight: '1.2'}}>
              Es muy frecuente que, cuando se comienza a establecer nuevos hábitos de vida, aparezcan durante unos días algunos síntomas como cefaleas, cansancio o dolores. No te alarmes. Esto no debe constituir un motivo para abandonar nuestras recomendaciones. Estos síntomas no suelen persistir más de 7–10 días.
            </div>
          </div>
        </main>
      </Page>

    </div>
  );
};

export default InformeSeguimientoView;


